import SharedProcessingTimeSection from "@/components/feature/SharedProcessingTimeSection";
import type { ProcessingOption } from "@/components/feature/SharedProcessingTimeSection";

const options: ProcessingOption[] = [
  {
    speed: "2–9",
    unit: "Business Days",
    label: "Fast Passport Center",
    tag: "Most Popular",
    tagColor: "bg-[#F6C344] text-[#1A365D]",
    features: [
      "Registered U.S. Department of State Courier",
      "Reserved agency submission — no 42-bridge drive to Miami",
      "Ideal for cruise passengers with tight embarkation deadlines",
      "Perfect for island residents flying out of EYW internationally",
      "Great for Key West tourism and hospitality professionals",
      "Document review included",
      "Dedicated agent assigned to your case",
      "Daily status updates — never left guessing",
      "Not affected by government shutdowns",
    ],
    highlight: true,
  },
  {
    speed: "3–5",
    unit: "Weeks",
    label: "Fast Passport Center",
    tag: "Standard Expedited",
    tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
    features: [
      "Registered U.S. Department of State Courier",
      "Reserved agency submission",
      "Faster than self-service at Monroe County post offices",
      "Good for planned international trips from Key West",
      "Document review included",
      "Dedicated agent assigned to your case",
      "Daily status updates — never left guessing",
      "Not affected by government shutdowns",
    ],
    highlight: false,
    smartSaver: true,
  },
  {
    speed: "1–2",
    unit: "Months",
    label: "Self-Service (Post Office)",
    tag: "Slowest",
    tagColor: "bg-gray-100 text-gray-400",
    features: [
      "No courier access — standard mail only",
      "No document review — errors cause rejection",
      "In-person clerk appointment required at Key West facilities",
      "Workload shifts with government shutdowns and budget fights",
      "No dedicated agent — you are on your own",
      "Updates only by physical mail letter",
      "No phone or email contact for your case",
      "Cannot upgrade speed after submission",
      "Not suitable if travel is within 10 weeks",
    ],
    highlight: false,
    muted: true,
  },
  {
    speed: "24",
    unit: "Hours*",
    label: "Regional Agency Processing",
    tag: "Emergency Only",
    tagColor: "bg-gray-100 text-gray-500",
    features: [
      "Closest regional agency: Miami — ~160 miles via US-1 Overseas Highway (42 bridges)",
      "NPIC appointment required before driving to Miami",
      "Life-or-death emergencies only",
      "Bereavement or medical crisis documentation required",
      "Expect the drive to take 4+ hours — and plan for bridge delays",
    ],
    highlight: false,
    emergency: true,
  },
];

export default function KeyWestProcessingTimeSection() {
  return (
    <SharedProcessingTimeSection
      sectionId="processing-options"
      cityName="Key West"
      headingExtra="FL"
      introText="Key West and Lower Keys travelers — cruise ship passengers sailing to the Caribbean and Bahamas on tight embarkation schedules, island residents flying out of Key West International (EYW) to international destinations, tourism and concierge professionals attending global hospitality conferences, Overseas Highway corridor families planning European vacations, and seasonal residents heading to their second homes abroad — have several options for getting a passport fast. Here is how they compare, and why most Keys residents choose a U.S. Department of State registered passport courier for predictable, professional processing without gambling on Miami across 42 bridges and 160 miles of single-thread highway."
      options={options}
      emergencyDescription="Key West sits at the end of US-1 — 113 miles of Overseas Highway threading across 42 bridges with zero alternative routes. One wreck on the Seven Mile Bridge, one flipped RV on Bahia Honda, and the entire Florida Keys are physically severed from the mainland for hours. A cruise passenger with a Royal Caribbean sailing departing Key West for Cozumel in 48 hours, or a Fantasy Fest organizer flying to an international carnival conference, cannot risk Miami's single agency across a 4-hour gauntlet of drawbridge openings, spring break traffic through Islamorada, and hurricane evacuation gridlock (June–November) that can turn the Overseas Highway into a one-way parking lot. During a Category 3+ evacuation, you are not driving to Miami — you are sheltering in place while the agency closes."
      disclaimerText="*Processing speed depends on eligibility and documentation. Key West does not have a regional passport agency — the closest is Miami, roughly 160 miles away across 42 bridges on the single-lane Overseas Highway. Not all applicants qualify for Regional Agency Processing."
    />
  );
}